import React, { useState, useEffect, useRef, useCallback } from "react";
import "./InstallPrompt.css";

function InstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const deferredPrompt = useRef(null);

  useEffect(() => {
    // Detect if already installed
    if (window.matchMedia("(display-mode: standalone)").matches) return;

    // Detect iOS
    const iOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    setIsIOS(iOS);

    // Check if dismissed within 24h
    const dismissed = localStorage.getItem("install-dismissed");
    if (dismissed) {
      const hoursSinceDismissed =
        (Date.now() - parseInt(dismissed, 10)) / 3600000;
      if (hoursSinceDismissed < 24) return;
    }

    // Capture beforeinstallprompt
    const handler = (e) => {
      e.preventDefault();
      deferredPrompt.current = e;
      setShowPrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // For iOS: show after delay
    if (iOS) {
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => {
        window.removeEventListener("beforeinstallprompt", handler);
        clearTimeout(timer);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = useCallback(async () => {
    if (isIOS) {
      // iOS doesn't support beforeinstallprompt, show instructions
      setShowPrompt(false);
      return;
    }

    if (deferredPrompt.current) {
      deferredPrompt.current.prompt();
      const { outcome } = await deferredPrompt.current.userChoice;
      deferredPrompt.current = null;
      if (outcome === "accepted") {
        setShowPrompt(false);
      }
    }
  }, [isIOS]);

  const handleDismiss = useCallback(() => {
    setShowPrompt(false);
    localStorage.setItem("install-dismissed", Date.now().toString());
  }, []);

  if (!showPrompt) return null;

  return (
    <div className="install-prompt" role="dialog" aria-label="Install app">
      <div className="install-prompt__bar">
        <button
          className="install-prompt__close"
          onClick={handleDismiss}
          aria-label="Close"
        >
          &times;
        </button>
        <div className="install-prompt__info">
          <svg
            className="install-prompt__icon"
            width="40"
            height="40"
            viewBox="0 0 64 64"
            fill="none"
          >
            <rect x="8" y="8" width="20" height="20" rx="4" fill="#0D9488" />
            <rect x="36" y="8" width="20" height="20" rx="4" fill="#0D9488" />
            <rect x="8" y="36" width="20" height="20" rx="4" fill="#0D9488" />
            <rect x="36" y="36" width="20" height="20" rx="4" fill="#0D9488" />
          </svg>
          <div>
            <span className="install-prompt__title">Install AxiomLabs</span>
            <span className="install-prompt__desc">
              {isIOS
                ? "Tap the share button then 'Add to Home Screen'"
                : "Add to your home screen for quick access"}
            </span>
          </div>
        </div>
        <div className="install-prompt__actions">
          {!isIOS && (
            <button
              className="install-prompt__btn install-prompt__btn--install"
              onClick={handleInstall}
            >
              Install
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default InstallPrompt;
